/**
 * What a node's valves are doing — and whether anybody has actually said.
 *
 * `relay_states` is an 8-bit mask, and every `field_nodes` row is created with
 * it at 0. Nothing on an XBee site reports it unprompted either: the bridge
 * posts a bitmask when it drives a relay, and when somebody runs a READ_STATES
 * (the node self-test, the mesh diagnostics). So the mask alone cannot tell
 *
 *     "all four valves are shut"          from    "nobody has ever asked"
 *
 * and a grid of four closed valves states the first while meaning the second.
 * That is the one statement a status page must not make about a valve.
 *
 * `relay_state_updated_at` is what separates them — the same column the
 * irrigation landing page reads to keep an unconfirmed run out of its "watering
 * now" count. This turns the pair into the three answers a card can honestly
 * give: open, closed, or not reported.
 *
 * Deliberately built on `relayCountFor`, not `usableRelays`: a relay an
 * operator has marked as wired to nothing is still driven by the hardware if a
 * schedule row names it, and hiding an energised valve is the worse failure.
 */

import { formatDuration, getRelayState, relayCountFor } from '../services/espnowIrrigation';

/** One valve: open, closed, or nothing has been heard about it. */
export type ValveReading = boolean | null;

export interface ValveReport {
  /** One entry per relay the board carries. `null` means never reported. */
  relays: ValveReading[];
  /** Valves known to be open. Zero when nothing has been reported. */
  openCount: number;
  /** Something has told us this node's output states at least once. */
  reported: boolean;
  /** When it did, if it said. */
  reportedAtMs: number | null;
}

interface NodeLike {
  relay_states?: number | null;
  relay_state_updated_at?: string | null;
  relay_count?: number | null;
  transport?: string | null;
}

export function valveReport(
  node: NodeLike | null | undefined,
  // Only for shapes that carry no transport of their own — the farm overview's
  // OverviewRouter, where it hangs off the controller. Same argument as
  // relayCountFor, which this passes it straight to.
  transport?: string | null,
): ValveReport {
  const count = relayCountFor(node, transport);
  const mask = node?.relay_states ?? 0;
  const at = node?.relay_state_updated_at ? Date.parse(node.relay_state_updated_at) : NaN;
  const reportedAtMs = Number.isFinite(at) ? at : null;

  // A non-zero mask with no timestamp is still somebody's report: a row written
  // by a server old enough to omit the column must not make an open valve
  // vanish. Zero with no timestamp is the silence this whole file is about.
  const reported = reportedAtMs !== null || mask > 0;

  const relays: ValveReading[] = Array.from({ length: count }, (_, i) =>
    reported ? getRelayState(mask, i) : null,
  );

  return {
    relays,
    openCount: relays.filter(Boolean).length,
    reported,
    reportedAtMs,
  };
}

/** The claim in words, for a card that has room for one line. */
export function valveSummary(report: ValveReport): string {
  if (!report.reported) return 'Valves not reported';
  if (report.openCount === 0) return 'All valves closed';
  return `${report.openCount} valve${report.openCount === 1 ? '' : 's'} open`;
}

/**
 * How old the reading is.
 *
 * Shown next to the claim because on these sites a bitmask is not a live feed —
 * it is whatever the last command or check left behind, which can be days old.
 * "All valves closed" with no age reads as a fresh sweep of the farm.
 */
export function valveAgeLabel(report: ValveReport, nowMs = Date.now()): string | null {
  if (report.reportedAtMs === null) return null;
  const sec = Math.max(0, Math.round((nowMs - report.reportedAtMs) / 1000));
  // formatDuration keeps counting in hours past a day, and "53h ago" is harder
  // to read than the number of days it is really saying.
  if (sec >= 172_800) return `${Math.floor(sec / 86_400)}d ago`;
  return `${formatDuration(sec)} ago`;
}
